// 2026-06-25: Buildings master page — mirrors Machine Group UX.
// Stores in TB customer attribute 'buildings' (array of building records).
import { useState, useEffect } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import './buildings.css';
import BuildingsAdd from './buildingsadd';
import BuildingsEdit from './buildingsedit';
import { customerbasedshift } from '../../Services/app/operatorservice';
import { shiftadd } from '../../Services/app/masterservice';
import Swal from 'sweetalert2';

const Buildings = ({ IsInGroupReg = false }) => {
  const [buildings, setBuildings] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [dialogOpenCount, setDialogOpenCount] = useState(0);
  const customerId = localStorage.getItem('CustomerID');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState(null);

  const handleCloseAddDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    setIsAddDialogOpen(false);
    setDialogOpenCount(prev => Math.max(0, prev - 1));
    fetchBuildings();
  };
  const handleCloseEditDialog = (event, reason) => {
    if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
    setIsEditDialogOpen(false);
    setEditDialogData(null);
    setDialogOpenCount(prev => Math.max(0, prev - 1));
    fetchBuildings();
  };

  useEffect(() => { fetchBuildings(); }, []);

  const fetchBuildings = async () => {
    customerbasedshift(customerId, 'buildings')
      .then(async (data) => {
        const all = data[0]?.value || [];
        setBuildings(Array.isArray(all) ? all : []);
      })
      .catch(error => {
        console.error('Error fetching buildings:', error);
        setBuildings([]);
      });
  };

  const handleOpenAddDialog = () => {
    setIsAddDialogOpen(true);
    setDialogOpenCount(prev => prev + 1);
  };
  const handleOpenEditDialog = (building) => {
    setEditDialogData(building);
    setIsEditDialogOpen(true);
    setDialogOpenCount(prev => prev + 1);
  };

  const handleDeleteBuilding = (row) => {
    Swal.fire({
      title: 'Delete this Site?',
      text: 'Machine Groups linked to this building will become "Unspecified".',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const updated = buildings.filter(b => (b.id || b.code) !== (row.id || row.code));
          await shiftadd({ buildings: updated }, customerId, 'SERVER_SCOPE');
          Swal.fire('Deleted', `Building "${row.name}" removed.`, 'success');
          fetchBuildings();
        } catch (error) {
          console.error('Error deleting building:', error);
          Swal.fire('Error!', 'Failed to delete: ' + error.message, 'error');
        }
      }
    });
  };

  return (
    <div className="pages" style={{ paddingBlockStart: IsInGroupReg ? '0px' : '40px' }}>
      <div className="pagecontents">
        <div className="left-labels" style={{ padding: IsInGroupReg ? '0 2rem 1rem 0' : '1rem 2rem 1rem 0' }}>
          <div className="shift-content-1">
            {IsInGroupReg ? (<h6>Create New</h6>) : (<h5>Sites</h5>)}
            <div className="add_new">
              <Tooltip title="Add Site">
                <IconButton className="circle" onClick={handleOpenAddDialog}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <div className="idle_reason_list">
          {Array.isArray(buildings) && buildings.length > 0 ? (
            buildings
              .slice()
              .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || String(a.code).localeCompare(String(b.code)))
              .map((b, idx) => {
                const itemId = b.id || b.code || idx;
                return (
                  <div className="idle_reason_item building_item" key={itemId} data-id={b.code}>
                    <div className="icons">
                      <span className="icon-text" title="Site code">{b.code}</span>
                    </div>
                    <div className="building_info">
                      <h3 className="reason-text">{b.name}</h3>
                      <div className="building_meta">
                        {b.plant_name && <span><strong>{b.plant_name}</strong></span>}
                        {b.plant_city && <span> · {b.plant_city}</span>}
                        {b.floor_count > 0 && <span> · {b.floor_count} floor{b.floor_count > 1 ? 's' : ''}</span>}
                        {b.gate_number && <span> · {b.gate_number}</span>}
                      </div>
                    </div>
                    <div className="user_action">
                      <ul>
                        <li>
                          <Tooltip title="Edit Site">
                            <IconButton onClick={() => handleOpenEditDialog(b)}>
                              <EditIcon sx={{ color: 'black' }} />
                            </IconButton>
                          </Tooltip>
                        </li>
                        <li>
                          <Tooltip title="Delete Site">
                            <IconButton onClick={() => handleDeleteBuilding(b)}>
                              <DeleteIcon sx={{ color: 'black' }} />
                            </IconButton>
                          </Tooltip>
                        </li>
                      </ul>
                    </div>
                  </div>
                );
              })
          ) : (
            <div style={{ margin: '2rem', color: '#888' }}>
              No buildings yet. Click <strong>+</strong> to add your first one.
            </div>
          )}
        </div>
      </div>

      <BuildingsAdd
        open={isAddDialogOpen}
        handleClose={handleCloseAddDialog}
        handleAdd={handleCloseAddDialog}
        dialogOpenCount={dialogOpenCount}
        datasource={buildings}
        customerId={customerId}
      />

      {editDialogData && (
        <BuildingsEdit
          open={isEditDialogOpen}
          handleClose={handleCloseEditDialog}
          handleEdit={handleCloseEditDialog}
          dialogOpenCount={dialogOpenCount}
          datasource={buildings}
          customerId={customerId}
          editData={editDialogData}
        />
      )}
    </div>
  );
};

export default Buildings;
